// Lista de colaboradores (uma linha por pessoa, com o vínculo atual) — substitui a aba
// "DP - geral" da planilha mãe. Filtros locais: a base inteira tem algumas centenas de pessoas.
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { FileSpreadsheet, Search, UserPlus } from "lucide-react";
import { getRhColaboradores } from "../../api";
import { formatData } from "../estoque/comum";
import { AvisoErro, BadgeAtivo, btnStyle, inputStyle, palette, useIsMobile } from "../estoque/ui";
import RhLayout from "./RhLayout";

const semAcento = (s) => String(s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

export default function ColaboradoresView() {
  const [isLight, setIsLight] = useState(true);
  const C = palette(isLight);
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [lista, setLista] = useState(null);
  const [erro, setErro] = useState("");
  const [busca, setBusca] = useState("");
  const [status, setStatus] = useState("Ativo");
  const [tipo, setTipo] = useState("");
  const [empresa, setEmpresa] = useState("");
  const [unidade, setUnidade] = useState("");

  useEffect(() => {
    getRhColaboradores().then(setLista).catch((e) => setErro(e.message));
  }, []);

  const valores = (campo) => Array.from(new Set((lista || []).map((l) => l[campo]).filter(Boolean))).sort();

  const filtrados = useMemo(() => {
    const termo = semAcento(busca.trim());
    return (lista || []).filter((l) =>
      (!termo || semAcento(`${l.nome} ${l.email_youx || ""} ${l.cargo || ""}`).includes(termo)) &&
      (!status || l.status === status) &&
      (!tipo || l.tipo_contratacao === tipo) &&
      (!empresa || l.empresa === empresa) &&
      (!unidade || l.unidade === unidade)
    );
  }, [lista, busca, status, tipo, empresa, unidade]);

  const porTipo = useMemo(() => {
    const contagem = {};
    filtrados.forEach((l) => { const t = l.tipo_contratacao || "Sem vínculo"; contagem[t] = (contagem[t] || 0) + 1; });
    return Object.entries(contagem).sort((a, b) => b[1] - a[1]);
  }, [filtrados]);

  function exportar() {
    const linhas = filtrados.map((l) => ({
      Nome: l.nome, "E-mail YOUX": l.email_youx || "", Tipo: l.tipo_contratacao || "", Empresa: l.empresa || "",
      Departamento: l.unidade || "", Cargo: l.cargo || "", Nível: l.nivel || "", Status: l.status || "",
      Admissão: formatData(l.data_admissao), Desligamento: l.data_desligamento ? formatData(l.data_desligamento) : "",
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(linhas), "Colaboradores");
    XLSX.writeFile(wb, "colaboradores.xlsx");
  }

  const filtro = (valor, setValor, rotuloTodos, opcoes) => (
    <select value={valor} onChange={(e) => setValor(e.target.value)} style={{ ...inputStyle(C), flex: "0 1 170px", width: "auto" }}>
      <option value="">{rotuloTodos}</option>
      {opcoes.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
  const th = (align = "left") => ({ textAlign: align, padding: "10px 12px", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: C.muted, borderBottom: `1px solid ${C.border}`, position: "sticky", top: 0, background: C.card });
  const td = (align = "left") => ({ textAlign: align, padding: "10px 12px", fontSize: 13, borderBottom: `1px solid ${C.border}` });

  return (
    <RhLayout titulo="Colaboradores" isLight={isLight} setIsLight={setIsLight}>
      <AvisoErro C={C} onClose={() => setErro("")}>{erro}</AvisoErro>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 12 }}>
        <div style={{ position: "relative", flex: "1 1 220px" }}>
          <Search size={15} style={{ position: "absolute", left: 12, top: 14, color: C.muted }} />
          <input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar por nome, e-mail ou cargo" style={{ ...inputStyle(C), paddingLeft: 34 }} />
        </div>
        {filtro(status, setStatus, "Todos os status", ["Ativo", "Inativo"])}
        {filtro(tipo, setTipo, "Todos os tipos", valores("tipo_contratacao"))}
        {filtro(empresa, setEmpresa, "Todas as empresas", valores("empresa"))}
        {filtro(unidade, setUnidade, "Todos os departamentos", valores("unidade"))}
        <button onClick={exportar} disabled={!filtrados.length} style={{ ...btnStyle(C.success), opacity: filtrados.length ? 1 : 0.5 }}>
          <FileSpreadsheet size={15} /> Excel
        </button>
        <button onClick={() => navigate("/rh/colaboradores/novo")} style={btnStyle(C.brand)}>
          <UserPlus size={15} /> Nova admissão
        </button>
      </div>

      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", fontSize: 12, color: C.muted, marginBottom: 10 }}>
        <strong style={{ color: C.text }}>{filtrados.length} pessoa(s)</strong>
        {porTipo.map(([t, n]) => <span key={t} style={{ padding: "1px 8px", borderRadius: 999, background: C.bgActive }}>{t}: {n}</span>)}
      </div>

      {lista === null && !erro && <div style={{ color: C.muted, fontSize: 13 }}>Carregando…</div>}

      {lista !== null && (isMobile ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {filtrados.map((l) => (
            <button key={l.id} onClick={() => navigate(`/rh/colaboradores/${l.id}`)} style={{ textAlign: "left", background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 12, color: C.text, fontFamily: "inherit", cursor: "pointer" }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                <div style={{ fontSize: 14, fontWeight: 700 }}>{l.nome}</div>
                {l.status && <BadgeAtivo C={C} ativo={l.status === "Ativo"} />}
              </div>
              <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
                {[l.tipo_contratacao, l.empresa, l.unidade, l.cargo].filter(Boolean).join(" · ")}
              </div>
            </button>
          ))}
        </div>
      ) : (
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, overflow: "auto", maxHeight: "70vh" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={th()}>Nome</th>
                <th style={th()}>Tipo</th>
                <th style={th()}>Empresa</th>
                <th style={th()}>Departamento</th>
                <th style={th()}>Cargo</th>
                <th style={th()}>Admissão</th>
                <th style={th("center")}>Status</th>
              </tr>
            </thead>
            <tbody>
              {filtrados.length === 0 && <tr><td colSpan={7} style={{ ...td("center"), color: C.muted, padding: 24 }}>Nenhum colaborador.</td></tr>}
              {filtrados.map((l) => (
                <tr key={l.id} onClick={() => navigate(`/rh/colaboradores/${l.id}`)} style={{ cursor: "pointer" }}>
                  <td style={{ ...td(), fontWeight: 600 }}>
                    {l.nome}
                    {l.email_youx && <div style={{ fontSize: 11, color: C.muted, fontWeight: 400 }}>{l.email_youx}</div>}
                  </td>
                  <td style={td()}>{l.tipo_contratacao || "—"}{l.qtd_contratos > 1 && <span style={{ fontSize: 11, color: C.muted }}> ({l.qtd_contratos} vínculos)</span>}</td>
                  <td style={td()}>{l.empresa || "—"}</td>
                  <td style={{ ...td(), color: C.muted }}>{l.unidade || "—"}</td>
                  <td style={td()}>{l.cargo || "—"}{l.nivel && <span style={{ color: C.muted }}> · {l.nivel}</span>}</td>
                  <td style={td()}>{formatData(l.data_admissao)}</td>
                  <td style={td("center")}>{l.status ? <BadgeAtivo C={C} ativo={l.status === "Ativo"} /> : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </RhLayout>
  );
}
